import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { App, hasActivatedProduct } from 'app/core/app/app.model';
import { AppService } from 'app/core/app/app.service';
import { AuthService } from 'app/core/auth/auth.service';
import { GameserverService } from 'app/modules/admin/fleet/gameserver.service';
import {
  acceptsInlineSuggestion,
  inlineSuggestion,
} from 'app/core/search/inline-suggestion';
import { scoreTerm } from 'app/core/search/rank-matches';
import {
  buildGlobalCommands,
  buildProjectCommands,
} from './command-palette.commands';
import {
  PaletteCommand,
  PaletteGroup,
  PaletteItem,
} from './command-palette.model';
import { CommandPaletteService } from './command-palette.service';
import { PaletteActionService, isActionLink } from './palette-action';
import { scoreCommand } from './command-palette.search';
import { FleetServerIndexService } from './fleet-server-index.service';

/** Label for the shortcut hint — `⌘K` on Apple keyboards, `Ctrl K` elsewhere. */
export const COMMAND_PALETTE_SHORTCUT = /Mac|iPhone|iPad|iPod/.test(
  navigator.userAgent,
)
  ? '⌘ K'
  : 'Ctrl K';

/** Projects listed with an empty query, before the ranking has anything to say. */
const PROJECT_PREVIEW_LIMIT = 8;

/** Rows kept per group once a query narrows things. */
const GROUP_RESULT_LIMIT = 8;

/**
 * Tie-break in favour of the project you are looking at.
 *
 * Deliberately the smallest number that can decide anything. The case it exists
 * for is "Servers" appearing once per project: those score *identically*,
 * because the query matched neither project's name, so a single point is enough
 * to put yours first.
 *
 * It was 1000 to begin with, and that quietly broke ranking altogether: any
 * page of the current project that matched a query at all outranked every
 * global command, so `new project` returned *Create Webhook* rather than
 * *New Project*. A tie-break must not be able to overturn a real difference.
 */
const HOME_PROJECT_BONUS = 1;

/**
 * Whether one of the query's terms is clearly naming this project.
 *
 * Deliberately stricter than the general matcher: only a prefix or a word start
 * counts (`scoreTerm` ≥ 100), and single characters are ignored. A loose match
 * here would drag a whole project's pages into the list on a stray letter,
 * which is the opposite of narrowing.
 */
function namesProject(terms: string[], name: string): boolean {
  const target = name.toLowerCase();
  return terms.some(
    (term) => term.length >= 2 && scoreTerm(term, target) >= 100,
  );
}

/** A scored command, as the group builder carries it around. */
interface ScoredCommand {
  command: PaletteCommand;
  score: number;
  /** Ranked below everything else — see {@link enteredOnlyAsFallback}. */
  demoted: boolean;
}

/**
 * Whether a row is in the list only because
 * {@link PaletteCommand.reachableAcrossProjects} let it in.
 *
 * Three ways a row reaches the pool while a project is filtered: it belongs to
 * that project, the query names its project, or it is a unique thing reachable
 * from anywhere. The first two were asked for; the third is a safety net, and a
 * safety net that outranks what the user actually filtered to is a nuisance.
 *
 * It happens easily, because scores say nothing about intent: inside *Nginx
 * Template*, `server` scored four Counter-Strike servers above the user's own
 * two, purely because that project's config is named "…Match Server" and a
 * title hit counts double. No constant can reliably out-add that — a term is
 * worth up to 240 there — so this is a sort key rather than a penalty.
 */
function enteredOnlyAsFallback(
  command: PaletteCommand,
  scopeId: string | null,
  terms: string[],
): boolean {
  return (
    !!scopeId &&
    !!command.reachableAcrossProjects &&
    command.appId !== scopeId &&
    !namesProject(terms, command.appName ?? '')
  );
}

/**
 * The row for the page the user is currently on, or `-1` when none of them is.
 *
 * Matched on the `routerLink` rather than on any stored history, because "what
 * I picked last time" and "where I am now" are the same thing here — the
 * palette navigates — and the route is the one that survives a reload, a
 * bookmark, or arriving from a link instead of from the palette.
 *
 * The longest match wins, so standing on `…/binary/create` selects *Create
 * Image* rather than *Images*, and a detail page (`…/server/821/info`, which no
 * row points at) falls back to the list it came from. Matching is by whole
 * segment: a link is a prefix of the URL only up to a `/`.
 *
 * Action rows are skipped. Their link carries `?action=create`, which asks the
 * target page to open a dialog; it is something to do, not somewhere to be, and
 * on `…/deployment` it would otherwise tie with *Deployments*.
 */
function indexOfCurrentPage(items: PaletteItem[], url: string): number {
  const path = url.split('?')[0].split('#')[0];
  let best = -1;
  let bestLength = 0;

  for (const item of items) {
    const link = item.command.routerLink;
    if (!link || link.includes('?')) continue;
    if (path !== link && !path.startsWith(`${link}/`)) continue;

    if (link.length > bestLength) {
      bestLength = link.length;
      best = item.index;
    }
  }

  return best;
}

/**
 * Whether a row flagged {@link PaletteCommand.onlyOnDirectMatch} earned its
 * place, rather than riding in on its project's name.
 *
 * Answered by scoring it a second time with `appName` blanked: if it still
 * matches, the query was about the row itself. Everything else passes straight
 * through, and an empty query always does — the unsearched list stays complete.
 */
function matchesDirectly(query: string, command: PaletteCommand): boolean {
  if (!command.onlyOnDirectMatch || !query) return true;

  return scoreCommand(query, { ...command, appName: undefined }) >= 0;
}

/**
 * Demoted rows last, then by score, then unique things before places.
 *
 * The last step decides only exact ties, and a tie here is a query that said
 * nothing about *which* row it meant: naming a project scores its pages and its
 * servers identically, because the only field either of them matched was the
 * project's name. Ordering that by how the list happens to be built put eight
 * pages in front of the servers, so searching a project's name — the obvious
 * way to reach its servers — showed everything except them.
 *
 * Servers win those ties because they are the rows that cannot be reached any
 * other way. The pages are the sidebar: two clicks away, and listed in full the
 * moment the palette opens with nothing typed. A term that actually names a
 * page still scores it higher and still leads.
 */
function byRank(a: ScoredCommand, b: ScoredCommand): number {
  return (
    Number(a.demoted) - Number(b.demoted) ||
    b.score - a.score ||
    Number(!!b.command.reachableAcrossProjects) -
      Number(!!a.command.reachableAcrossProjects)
  );
}

/**
 * Trim one group to {@link GROUP_RESULT_LIMIT} rows *of each kind*, keeping the
 * incoming order within each.
 *
 * The cap protects against repetition, and only one kind of row repeats: a page
 * exists in every project, so twenty copies of `Images` are scroll rather than
 * results. Rows flagged {@link PaletteCommand.reachableAcrossProjects} name a
 * thing that exists exactly once, so they are never that kind of noise — and
 * putting them under the same cap meant the noise could push them out entirely.
 *
 * Both pots are merged back by score, so a strong hit still leads its group;
 * the split decides only what survives, never what comes first.
 */
function capPerKind(
  matches: ScoredCommand[],
  homeId: string | null,
): ScoredCommand[] {
  const places: ScoredCommand[] = [];
  const things: ScoredCommand[] = [];
  let foreignPlaces = 0;

  for (const match of matches) {
    if (match.command.reachableAcrossProjects) {
      if (things.length < GROUP_RESULT_LIMIT) {
        things.push(match);
      }
      continue;
    }

    // The pages of the project the user is standing in are never trimmed. They
    // are not the repetition this guards against — they are one project's
    // sidebar, a bounded set already on screen beside the palette. Capping them
    // cut `Servers`, ninth in Fleet's list, out of a search for the project's
    // own name: the servers were listed while the page they live on was not.
    //
    // Keyed on the *home* project rather than the filter chip, the same value
    // `HOME_PROJECT_BONUS` uses. Removing the chip does not mean leaving the
    // project — you are still on its Servers page, and the list still has to
    // contain it. Off any project (the project list, sign-in) there is no home
    // and everything is capped, which is right: nothing there is more yours
    // than anything else.
    if (homeId && match.command.appId === homeId) {
      places.push(match);
      continue;
    }

    if (foreignPlaces < GROUP_RESULT_LIMIT) {
      places.push(match);
      foreignPlaces++;
    }
  }

  return [...places, ...things].sort(byRank);
}

/**
 * The group heading matching the page the user is on, or `null` off any of
 * them (the projects list, the sign-in shell).
 *
 * Derived from the URL rather than from router state objects because that is
 * the one thing every section here already agrees on, and the mapping is then
 * readable in one glance. The project-scoped `settings` test has to come before
 * the account-level one, which is why that pattern is anchored.
 */
function leadingGroupFor(url: string): string | null {
  const path = url.split('?')[0];

  for (const [pattern, group] of [
    [/\/projects\/[^/]+\/fleet(\/|$)/, 'Fleet'],
    [/\/projects\/[^/]+\/voice(\/|$)/, 'Voice'],
    [/\/projects\/[^/]+\/rooms(\/|$)/, 'Rooms'],
    [/\/projects\/[^/]+\/cortex(\/|$)/, 'Cortex'],
    [/\/projects\/[^/]+\/settings(\/|$)/, 'Project Settings'],
    [/^\/settings(\/|$)/, 'Account'],
  ] as const) {
    if (pattern.test(path)) return group;
  }

  return null;
}

/**
 * Quick-access / command palette.
 *
 * Mounted once in the layout shell (next to `<online-help>`) so the
 * `Ctrl`/`⌘` + `K` shortcut works on every screen without each feature area
 * wiring it up. The list itself comes from
 * {@link buildProjectCommands} / {@link buildGlobalCommands} — see that file for
 * what is offered and why.
 *
 * The palette is **app-wide**, and reaches other projects three ways, none of
 * which is a mode you can get stuck in:
 *
 * - Name the project in the query. `alpine servers` finds Alpine's servers from
 *   anywhere, in one go — see {@link _pool}.
 * - Take the chip off (`⌫` on an empty field, or its `×`) and search every
 *   project at once, each row labelled with the project it belongs to.
 * - Pick a project row, which moves the chip to it.
 *
 * The chip starts on the project the console is showing, because that is what
 * nearly every search is about; it is a filter, not a location.
 *
 * The shortcut is gated on there being a Fusion session: the shell also renders
 * the `empty` layout used by sign-in, and a palette full of guarded routes is
 * useless to a guest.
 */
@Component({
  selector: 'command-palette',
  standalone: true,
  imports: [MatIconModule, MatProgressSpinnerModule],
  templateUrl: './command-palette.component.html',
  styleUrls: ['./command-palette.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommandPaletteComponent {
  private readonly _appService = inject(AppService);
  private readonly _authService = inject(AuthService);
  private readonly _commandPalette = inject(CommandPaletteService);
  private readonly _dialog = inject(MatDialog);
  private readonly _document = inject(DOCUMENT);
  private readonly _gameserverService = inject(GameserverService);
  private readonly _fleetServerIndex = inject(FleetServerIndexService);
  private readonly _paletteActions = inject(PaletteActionService);
  private readonly _router = inject(Router);

  private readonly _queryInput =
    viewChild<ElementRef<HTMLInputElement>>('queryInput');

  private readonly _activeApp = toSignal(this._appService.activeApp$, {
    initialValue: null,
  });

  private readonly _apps = toSignal(this._appService.apps$, {
    initialValue: [] as App[] | null,
  });

  /** Raw highlight position. Read through {@link activeIndex}, never directly. */
  private readonly _activeIndex = signal(0);
  /** Row to scroll to once the panel has rendered. See the open effect. */
  private _pendingScrollIndex: number | null = null;
  /**
   * Whether the pointer has actually moved since this opening.
   *
   * The panel appears centred, under wherever the pointer happened to be
   * resting, and the row it lands on fires `mouseenter` for free — which read
   * as a hover and took the highlight off the row the palette had just chosen.
   * Opening on a Fleet page with the pointer parked mid-screen selected *API
   * Keys*, ten rows away from anything the user had touched.
   *
   * A resting pointer is not a hover. Hovering resumes the moment the mouse is
   * actually moved, which is the only moment the user means it.
   */
  private _pointerMoved = false;

  /**
   * The project filter, as three distinct states — which is why this is not a
   * plain `string | null`:
   *
   * - `undefined` — untouched, so follow whatever project the console is on.
   * - a project id — pinned to that project.
   * - `null` — deliberately cleared, meaning *search every project*.
   *
   * Collapsing "untouched" and "cleared" is what made the chip feel stuck: the
   * inherited chip had nothing to clear *to*, because clearing it immediately
   * fell back to the same project.
   *
   * Read through {@link scopeApp}.
   */
  private readonly _scopeOverride = signal<string | null | undefined>(
    undefined,
  );

  /** Where focus was before the palette opened, so it can be handed back. */
  private _returnFocusTo: HTMLElement | null = null;

  /** Previous value of {@link busy}, so the effect below sees the edge. */
  private _wasBusy = false;

  /** Clear a command query once its delayed action has finished. */
  private _resetQueryOnClose = false;

  /** Requests already issued for the current availability refresh. */
  private _fleetAvailabilityRequested = new Set<string>();

  /** Previous open state, used to refresh without discarding visible state. */
  private _wasPaletteOpen = false;

  /**
   * The group to lead with, captured when the palette opens.
   *
   * A signal rather than a read of `router.url`, because {@link groups} is a
   * `computed` and the router's URL is not reactive. Opening is the only moment
   * it can have changed — navigating closes the palette.
   */
  private readonly _leadingGroup = signal<string | null>(null);

  readonly isOpen = this._commandPalette.isOpen;

  readonly query = signal('');

  readonly shortcutLabel = COMMAND_PALETTE_SHORTCUT;

  /** True while a chosen action's page is still loading. */
  readonly busy = this._paletteActions.pending;

  /** Command whose row carries the in-place loading spinner. */
  readonly busyCommandId = signal<string | null>(null);

  /** Setup facts confirmed for each Fleet project. */
  private readonly _fleetAvailability = signal<
    ReadonlyMap<string, { hasImages?: boolean; hasServerConfigs?: boolean }>
  >(new Map());

  /** Every project the user has. Already in memory — `initialDataResolver` loaded it. */
  readonly apps = computed<App[]>(() => this._apps() ?? []);

  /**
   * The project the palette is filtered to, or `null` for "all of them".
   *
   * Opening the palette untouched shows where you already are, which is the
   * common case; from there the chip is a filter like any other — removable,
   * and removing it widens the search rather than doing nothing.
   */
  readonly scopeApp = computed<App | null>(() => {
    const override = this._scopeOverride();
    if (override === undefined) {
      return this._activeApp();
    }
    if (override === null) {
      return null;
    }
    return this.apps().find((app) => app.id === override) ?? null;
  });

  /** Every project's pages, built once per project rather than per keystroke. */
  private readonly _commandsByApp = computed(() => {
    const fleetAvailability = this._fleetAvailability();
    const servers = this._fleetServerIndex.servers();
    // Individual servers are built only while something is typed — see
    // `searching` in `command-palette.commands.ts`. Reading the query here is
    // what makes those rows appear and disappear with it.
    const searching = this.query().trim().length > 0;

    return this.apps().map((app) => ({
      app,
      commands: buildProjectCommands(app, {
        ...fleetAvailability.get(app.id),
        servers: servers.get(app.id),
        searching,
      }),
    }));
  });

  /** Account-level commands plus one row per project. */
  private readonly _globalCommands = computed(() =>
    buildGlobalCommands(this.apps(), this.scopeApp()?.id ?? null),
  );

  /**
   * The pages currently searchable, across however many projects are in play.
   *
   * Three rules, and between them they are what makes the palette reach
   * everything without ever putting the user in a mode they have to escape:
   *
   * 1. The filtered project's pages are always in. That is the common case and
   *    it stays as short as it was.
   * 2. Naming another project in the query pulls its pages in *as well* — so
   *    `alpine servers` reaches Alpine's servers from inside any project, in
   *    one query, without touching the chip. It works because `scoreFields`
   *    requires every term to match: `alpine` fails against the current
   *    project's pages and passes against Alpine's, so the list narrows to
   *    exactly what was asked for.
   * 3. With no filter at all, every project is in — that is what removing the
   *    chip means.
   *
   * Unsearched, foreign pages stay out: a list of every page of every project
   * is not something anyone scrolls.
   *
   * Rule 2 has one exception, and it is the reason
   * {@link PaletteCommand.reachableAcrossProjects} exists. Naming the project
   * is a fair price for a *page*, because the same page exists in all of them
   * and the name is the only thing that narrows it. It is an impossible price
   * for a server: its id exists in exactly one project, and someone searching
   * `821` is searching precisely because they do not know which. Those rows
   * are therefore searchable from anywhere, while their project's pages still
   * are not.
   */
  private readonly _pool = computed<PaletteCommand[]>(() => {
    const terms = this.query()
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);
    const scopeId = this.scopeApp()?.id ?? null;

    const pool: PaletteCommand[] = [];
    for (const { app, commands } of this._commandsByApp()) {
      const included =
        app.id === scopeId ||
        (terms.length > 0 && (!scopeId || namesProject(terms, app.name)));
      if (included) {
        pool.push(...commands);
      } else if (terms.length > 0) {
        pool.push(...commands.filter((c) => c.reachableAcrossProjects));
      }
    }

    pool.push(...this._globalCommands());
    return pool;
  });

  /**
   * Matching commands, grouped for display and numbered for keyboard nav.
   *
   * Unsearched, the list is **complete and always in the same order**: every
   * group in registry order (it reads like the sidebars), with the section the
   * user is currently in moved to the front. Nothing is hidden and nothing
   * moves between openings, which is what lets the list be learned — `⌘K`,
   * three arrows down, Enter, without reading.
   *
   * There used to be a `Recent` group above all this, and it worked against
   * exactly that: to avoid listing a page twice it *removed* its entries from
   * their own groups, so the Fleet section silently lost Images and Create
   * Image the moment they had been used. A group that is missing entries is
   * worse than no shortcut at all.
   *
   * Once the user types, groups are ordered by their best member so the
   * strongest match is always the first row — which is also what floats
   * `Projects` to the top the moment someone types another project's name.
   */
  readonly groups = computed<PaletteGroup[]>(() => {
    const query = this.query().trim();
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    const scopeId = this.scopeApp()?.id ?? null;
    const homeId = scopeId ?? this._activeApp()?.id ?? null;

    const matches = this._pool()
      .map((command) => ({ command, score: scoreCommand(query, command) }))
      // Drop non-matches *before* the bonus below. Adding it first turned every
      // `-1` on the current project into a large positive number, so a query
      // that matched nothing still returned that project's entire page list.
      .filter((match) => match.score >= 0)
      .filter((match) => matchesDirectly(query, match.command))
      .map((match) => ({
        command: match.command,
        // Unfiltered, the same page exists in every project. Keeping the one
        // you are actually looking at on top means the unqualified query still
        // does the obvious thing.
        score:
          match.score +
          (match.command.appId && match.command.appId === homeId
            ? HOME_PROJECT_BONUS
            : 0),
        demoted: enteredOnlyAsFallback(match.command, scopeId, terms),
      }));

    const byGroup = new Map<string, typeof matches>();
    for (const match of matches) {
      const bucket = byGroup.get(match.command.group);
      if (bucket) {
        bucket.push(match);
      } else {
        byGroup.set(match.command.group, [match]);
      }
    }

    // Unsearched, the full project list would bury everything below it, so show
    // a slice. Once there is a query the ranking decides and the cap is off.
    if (!query) {
      const projects = byGroup.get('Projects');
      if (projects) {
        byGroup.set('Projects', projects.slice(0, PROJECT_PREVIEW_LIMIT));
      }
    }

    const grouped = [...byGroup].map(([title, groupMatches]) => ({
      title,
      groupMatches,
      best: Math.max(...groupMatches.map((match) => match.score)),
    }));

    if (query) {
      grouped.sort((a, b) => b.best - a.best);
      for (const group of grouped) {
        group.groupMatches.sort(byRank);
        // Without a filter one group can hold the same page from every
        // project; past a handful they stop being results and start being
        // scroll. Naming the project is what narrows them.
        //
        // Counted in two pots, because the reason for the cap only applies to
        // one of them. Pages repeat across projects, which is what makes a long
        // run of them worthless; a server exists exactly once. Sharing a single
        // cap let the repetition crowd out the unique rows: naming a project
        // scored *all twelve of its pages identically* — the term matched only
        // their `appName` — and twelve ties filled the cap before a single
        // server was reached, so `nginx` listed everything about the project
        // except the servers running in it.
        group.groupMatches = capPerKind(group.groupMatches, homeId);
      }
    } else {
      // Lead with the section the user is standing in: opening the palette
      // from a Fleet page should put Fleet's pages under the cursor, not four
      // groups below it. Everything else keeps registry order, so the list is
      // the same every time you open it from the same place.
      const leading = this._leadingGroup();
      const at = grouped.findIndex((group) => group.title === leading);
      if (at > 0) {
        grouped.unshift(...grouped.splice(at, 1));
      }
    }

    // Number the rows only after the final order is settled — the index is what
    // the arrow keys and `aria-activedescendant` address.
    let index = 0;
    return grouped.map((group) => ({
      title: group.title,
      items: group.groupMatches.map((match) => ({
        command: match.command,
        index: index++,
        // The project is shown only when the chip is not already saying it.
        meta:
          match.command.appId && match.command.appId !== scopeId
            ? match.command.appName
            : match.command.meta,
      })),
    }));
  });

  /** All visible rows, flattened — the list the arrow keys walk. */
  readonly items = computed<PaletteItem[]>(() =>
    this.groups().flatMap((group) => group.items),
  );

  /**
   * The dimmed remainder shown behind the field, same as the projects page and
   * the project switcher.
   *
   * Candidates come from {@link items}, which is already ranked, so the best
   * prefix match wins — and because project rows share that pool, typing a
   * project's name completes it too.
   */
  readonly suggestion = computed(() =>
    inlineSuggestion(
      this.query(),
      this.items().map((item) => item.command.title),
    ),
  );

  /**
   * Highlighted row, clamped to what is actually on screen.
   *
   * Clamping here rather than writing the signal back keeps the result correct
   * when the command list shrinks underneath it (switching project, a filtered
   * query) without needing an effect that writes state.
   */
  readonly activeIndex = computed(() => {
    const count = this.items().length;
    return count ? Math.min(this._activeIndex(), count - 1) : -1;
  });

  constructor() {
    // Focus the field as soon as the panel exists. A dismissed palette keeps
    // its query selected; a command that navigated cleared it before this next
    // opening, ready for a fresh search.
    //
    // Opening is handled here rather than in a method because there are two
    // entry points — the shortcut below and the header button, which calls the
    // service directly — and both need the same setup.
    effect(() => {
      if (!this.isOpen()) return;

      if (!this._returnFocusTo) {
        const active = this._document.activeElement;
        this._returnFocusTo = active instanceof HTMLElement ? active : null;
        this._leadingGroup.set(leadingGroupFor(this._router.url));
        // Untracked: this reads the whole command list, and the effect must not
        // re-run — and re-place the highlight under the user — every time that
        // list settles.
        untracked(() => this._startOnCurrentPage());
        this._watchForPointerMovement();
      }

      const input = this._queryInput()?.nativeElement;
      input?.focus();
      input?.select();

      // Deferred until the panel exists. The first run of this effect can
      // happen before the view child resolves, and a row that is not rendered
      // yet cannot be scrolled to.
      if (input && this._pendingScrollIndex !== null) {
        this._scrollIntoView(this._pendingScrollIndex);
        this._pendingScrollIndex = null;
      }

      // The active project only, and only if its entry has aged. Opening must
      // never fan out across projects — that is the cost this index exists to
      // keep off the shortcut.
      this._fleetServerIndex.ensureFresh(this._activeApp());
    });

    // The project list has neither image nor server-config counts. Start loading
    // the active Fleet app immediately, before the palette is opened, so a
    // disabled action does not first flash as available. Opening refreshes the
    // relevant scope but keeps the last known state visible until it answers.
    effect(() => {
      const isOpen = this.isOpen();
      const activeApp = this._activeApp();
      const scopeApp = this.scopeApp();

      if (isOpen && !this._wasPaletteOpen) {
        this._fleetAvailabilityRequested.clear();
      }

      const apps = isOpen
        ? scopeApp
          ? [scopeApp]
          : this.apps()
        : activeApp
          ? [activeApp]
          : [];

      for (const app of apps) {
        if (
          !hasActivatedProduct(app, 'fleet') ||
          this._fleetAvailabilityRequested.has(app.id)
        ) {
          continue;
        }

        const fleetAppId = Number.parseInt(app.appId ?? '', 10);
        if (!fleetAppId) continue;

        this._fleetAvailabilityRequested.add(app.id);
        this._gameserverService.getAppBinaries(fleetAppId, 1, 1).subscribe({
          next: (response) => {
            this._setFleetAvailability(app.id, {
              hasImages: response.data.length > 0,
            });
          },
          // An unavailable check must not turn a usable destination into a
          // dead one. The target page will show its normal request error.
          error: () => undefined,
        });
        this._gameserverService.getServerConfigs(fleetAppId, 1, 1).subscribe({
          next: (response) => {
            this._setFleetAvailability(app.id, {
              hasServerConfigs: response.data.length > 0,
            });
          },
          error: () => undefined,
        });
      }

      this._wasPaletteOpen = isOpen;
    });

    // The other half of the spinner: the page has answered, so get out of the
    // way. By this point the dialog is already up behind the panel.
    effect(() => {
      const busy = this.busy();
      if (this._wasBusy && !busy && this.isOpen()) {
        this.close();
      }
      if (!busy) {
        this.busyCommandId.set(null);
      }
      this._wasBusy = busy;
    });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Keyboard
  // -----------------------------------------------------------------------------------------------------

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    // Ctrl+K / ⌘K toggles from anywhere, including from inside a text field —
    // that is the behaviour people expect from this shortcut. `preventDefault`
    // is what stops Chrome from hijacking it for the address bar.
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
      if (!this._authService.fusionSid) return;

      // A modal dialog outranks the palette. It sits in the CDK overlay
      // container, which stacks above this panel, so opening underneath it
      // would be invisible — and worse, the palette navigates, which would
      // discard whatever half-filled form is up there. Some of those dialogs
      // are `disableClose`, so this would also have been a way around a
      // deliberate "you must answer this".
      if (!this.isOpen() && this._dialog.openDialogs.length) return;

      event.preventDefault();
      if (this.isOpen()) {
        this.close(true);
      } else {
        this._commandPalette.open();
      }
      return;
    }

    if (!this.isOpen()) return;

    // Mid-navigation the list underneath is already stale, so the only key that
    // still means anything is the one that calls the whole thing off.
    if (this.busy() && event.key !== 'Escape') {
      event.preventDefault();
      return;
    }

    switch (event.key) {
      case 'Escape':
        // Always the panel, never the filter. An Escape that closes one thing
        // sometimes and another thing the rest of the time is not predictable;
        // Backspace is the key that takes the chip off.
        event.preventDefault();
        this.close(true);
        break;
      case 'Backspace':
        // Backspace on an empty field deletes the thing to the left of the
        // caret, and the chip is what is there — the same behaviour as a
        // recipient chip in a mail client.
        if (this.query() === '' && this.scopeApp()) {
          event.preventDefault();
          this.clearScope();
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        this._move(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this._move(-1);
        break;
      case 'Home':
        event.preventDefault();
        this._moveTo(0);
        break;
      case 'End':
        event.preventDefault();
        this._moveTo(this.items().length - 1);
        break;
      case 'Enter': {
        event.preventDefault();
        const item = this.items()[this.activeIndex()];
        if (item) this.run(item.command);
        break;
      }
      case 'Tab':
      case 'ArrowRight':
        // Tab and Right-arrow accept the inline completion, exactly as they do
        // on the projects page. When there is nothing to accept, Tab is still
        // swallowed — nothing else in the panel is focusable, so letting it
        // through would only move focus into the page behind it — while
        // Right-arrow is left alone so it keeps moving the caret.
        if (acceptsInlineSuggestion(event, this.query(), this.suggestion())) {
          event.preventDefault();
          this.query.set(this.suggestion());
          this._activeIndex.set(0);
        } else if (event.key === 'Tab') {
          event.preventDefault();
        }
        break;
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Close the palette.
   *
   * @param restoreFocus Whether to hand focus back to whatever opened it. True
   *   for a keyboard dismissal, where focus would otherwise land on a detached
   *   node and the next Tab would restart at the top of the document. **False
   *   for a click**, and that distinction is the point: Material paints a
   *   persistent focus ring on a programmatically focused button, so restoring
   *   focus after a backdrop click left the header's Quick access button
   *   looking pressed until the user clicked somewhere else.
   */
  close(restoreFocus = false): void {
    this._commandPalette.close();
    this._scopeOverride.set(undefined);
    // Escape during the wait cancels it. Harmless when the settle came from
    // the page itself — this only clears an already-clear flag.
    this._paletteActions.settle();
    this.busyCommandId.set(null);

    if (this._resetQueryOnClose) {
      this.query.set('');
      this._activeIndex.set(0);
      this._resetQueryOnClose = false;
    }

    if (restoreFocus) {
      this._returnFocusTo?.focus();
    }
    this._returnFocusTo = null;
    this._stopWatchingForPointerMovement();
  }

  onQueryInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;

    this.query.set(value);
    this._activeIndex.set(0);

    // Typing is the point at which the index has to be right, and the point at
    // which the user has said they are looking for something rather than
    // walking to a page. Rebuilding it is guarded by its own interval and does
    // not block: the rows on screen come from what is already held, and the
    // list fills in as answers land.
    if (value.trim()) {
      this._fleetServerIndex.refreshAll();
    }
  }

  highlight(index: number): void {
    // A row that arrived under a resting pointer is not being hovered.
    if (!this._pointerMoved) return;

    this._activeIndex.set(index);
  }

  /** Take the chip off, widening the search to every project. */
  clearScope(): void {
    this._scopeOverride.set(null);
    this._activeIndex.set(0);
    this._queryInput()?.nativeElement.focus();
  }

  /** Close and navigate, or — for a project row — filter the palette to it. */
  run(command: PaletteCommand): void {
    if (this.busy() || command.disabled) return;

    if (command.scopeAppId) {
      this._scopeOverride.set(command.scopeAppId);
      this.query.set('');
      this._activeIndex.set(0);
      this._queryInput()?.nativeElement.focus();
      return;
    }

    if (command.url) {
      this._resetQueryOnClose = true;
      this.close();
      window.open(command.url, '_blank', 'noopener');
      return;
    }

    if (!command.routerLink) return;

    // A command that opens a dialog keeps the palette up, showing a spinner,
    // until the target page reports back. The page it lands on has to load and
    // fetch before that dialog can exist — several seconds for Create Deployment —
    // and closing first turned that wait into a dead screen where nothing
    // acknowledged the click. Plain navigation closes immediately as before.
    if (isActionLink(command.routerLink)) {
      this._resetQueryOnClose = true;
      this.busyCommandId.set(command.id);
      this._paletteActions.start();
    } else {
      this._resetQueryOnClose = true;
      this.close();
    }

    // No `setActiveApp` here even when the target is another project:
    // `appContextResolver` derives the active project from `:projectId` on
    // the way in, so setting it first would only be a second source of truth.
    void this._router.navigateByUrl(command.routerLink).then((navigated) => {
      if (!navigated) {
        this._paletteActions.settle();
      }
    });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /** Merge one Fleet availability response without discarding the other one. */
  private _setFleetAvailability(
    appId: string,
    update: { hasImages?: boolean; hasServerConfigs?: boolean },
  ): void {
    this._fleetAvailability.update((availability) => {
      const next = new Map(availability);
      next.set(appId, { ...next.get(appId), ...update });
      return next;
    });
  }

  /**
   * Open with the highlight on the page the user is already on.
   *
   * Reopening always started at the top, so every second move meant arrowing
   * back down past everything: pick *Deployments*, land there, reopen, and
   * *Servers* — the next row in the sidebar — was nine presses away again. From
   * the current page a neighbour is one press, which is what the sidebar
   * ordering was for.
   *
   * Only with an empty query. Once something is typed the first row is the best
   * match, and moving the highlight off it would be wrong.
   */
  private _startOnCurrentPage(): void {
    const index = this.query().trim()
      ? -1
      : indexOfCurrentPage(this.items(), this._router.url);

    this._activeIndex.set(Math.max(index, 0));
    this._pendingScrollIndex = index > 0 ? index : null;
  }

  /** Arm {@link _pointerMoved}, cleared again on the next real mouse move. */
  private _watchForPointerMovement(): void {
    this._pointerMoved = false;
    // `once`, because one movement is all it takes: from then on the pointer is
    // where the user put it, and hovering means what it says for the rest of
    // this opening.
    this._document.addEventListener('mousemove', this._onPointerMove, {
      once: true,
    });
  }

  private _stopWatchingForPointerMovement(): void {
    this._document.removeEventListener('mousemove', this._onPointerMove);
  }

  private readonly _onPointerMove = (event: MouseEvent): void => {
    this._pointerMoved = true;

    // The row under the pointer had its own `mouseenter` suppressed — that
    // event fires before this one, and before any movement has happened — so
    // it is picked up here. Without this it would stay unhighlighted until the
    // pointer left the row and came back, even while sitting on it.
    const row = (event.target as Element | null)?.closest(
      '.command-palette-item',
    );
    const index = Number(row?.id.replace('command-palette-item-', ''));

    if (row && Number.isInteger(index)) {
      this._activeIndex.set(index);
    }
  };

  /** Bring a row into view, if it is rendered. */
  private _scrollIntoView(index: number): void {
    this._document
      .getElementById(`command-palette-item-${index}`)
      ?.scrollIntoView({ block: 'nearest' });
  }

  /** Move the highlight by `delta`, wrapping at both ends. */
  private _move(delta: number): void {
    const count = this.items().length;
    if (!count) return;

    this._moveTo((this.activeIndex() + delta + count) % count);
  }

  /** Highlight `index` and scroll it into view. */
  private _moveTo(index: number): void {
    if (index < 0) return;

    this._activeIndex.set(index);

    // The whole list is rendered, so the row already exists in the DOM.
    this._scrollIntoView(index);
  }
}
